import {
    CheckMerge,
    OutstandingCashItem,
    TransactionForReconciliationFromStatement,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import {
    getStatmentTransactionId,
    PendingCheckMergeForBalance,
} from "@common/helpers/reconciliation";
import {
    getMatchedTransactionDates,
    GetMatchedTransactionDatesParams,
} from "@common/helpers/reconciliationMatchedTransactionDates";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { pluralizeWithCount } from "@common/helpers/string";
import { partition } from "@common/helpers/collections";
import { BankCashItemType } from "@common/types/bankCashItem";
import { ReconcileTransactionsPayload } from "@api/reconciliation.api";
import { CheckMergeSelection } from "./Reconciliation.context";

interface GetStatementTransactionsToAddParams {
    pairs: TransactionsPairForReconciliation[];
    statementTransactionsToAddIdsSet: Set<string>;
}

interface BuildReconcilePayloadParams
    extends
        GetStatementTransactionsToAddParams,
        GetMatchedTransactionDatesParams {
    financialAccountId: number;
    financialTransactionsToRemoveIds: number[];
    financialTransactionsFromPreviousPeriodIds: number[];
    checkMerges: CheckMergeSelection[];
}

interface ToPendingCheckMergesForBalanceParams {
    checkMerges: CheckMergeSelection[];
    outstandingCashItems: OutstandingCashItem[];
}

interface CashItemsTotal {
    count: number;
    amount: number;
}

interface OutstandingCashItemsTotals {
    checks: CashItemsTotal;
    deposits: CashItemsTotal;
}

interface GetUnmatchedCashItemsParams {
    cashItems: OutstandingCashItem[];
    checkMerges: CheckMergeSelection[];
}

export function getStatementTransactionsToAdd({
    pairs,
    statementTransactionsToAddIdsSet,
}: GetStatementTransactionsToAddParams): TransactionForReconciliationFromStatement[] {
    return pairs.flatMap(({ fromStatement }) =>
        fromStatement &&
        statementTransactionsToAddIdsSet.has(
            getStatmentTransactionId(fromStatement),
        )
            ? [fromStatement]
            : [],
    );
}

export function getNewDateByTransactionId(
    params: GetMatchedTransactionDatesParams,
): Map<number, string> {
    const newDateByTransactionId = new Map<number, string>();
    for (const {
        financialTransactionId,
        newDate,
    } of getMatchedTransactionDates(params)) {
        if (newDate) {
            newDateByTransactionId.set(financialTransactionId, newDate);
        }
    }
    return newDateByTransactionId;
}

export function buildReconcilePayload({
    financialAccountId,
    pairs,
    financialTransactionsToRemoveIds,
    statementTransactionsToAddIdsSet,
    financialTransactionIdsIgnoringDateChanges,
    financialTransactionsFromPreviousPeriodIds,
    checkMerges,
}: BuildReconcilePayloadParams): ReconcileTransactionsPayload {
    const statementLinesById = new Map(
        getStatementTransactionsToAdd({
            pairs,
            statementTransactionsToAddIdsSet,
        }).map((line) => [getStatmentTransactionId(line), line]),
    );
    const mergedStatementTransactionIds = new Set(
        checkMerges.flatMap((merge) =>
            "statementTransactionId" in merge
                ? [merge.statementTransactionId]
                : [],
        ),
    );

    return {
        financialAccountId,
        financialTransactionsToRemove: financialTransactionsToRemoveIds,
        statementTransactionsToAdd: [...statementLinesById].flatMap(
            ([statementTransactionId, line]) =>
                mergedStatementTransactionIds.has(statementTransactionId)
                    ? []
                    : [toStatementTransactionToAdd(line)],
        ),
        reconciledTransactions: [
            ...getMatchedTransactionDates({
                pairs,
                financialTransactionIdsIgnoringDateChanges,
            }),
            ...financialTransactionsFromPreviousPeriodIds.map((id) => ({
                financialTransactionId: id,
                newDate: undefined,
            })),
        ],
        checkMerges: checkMerges.flatMap((merge): CheckMerge[] => {
            if ("bankTransactionId" in merge) {
                return [
                    {
                        checkTransactionId: merge.checkTransactionId,
                        bankTransactionId: merge.bankTransactionId,
                    },
                ];
            }
            const statementLine = statementLinesById.get(
                merge.statementTransactionId,
            );
            return statementLine
                ? [
                      {
                          checkTransactionId: merge.checkTransactionId,
                          statementLine:
                              toStatementTransactionToAdd(statementLine),
                      },
                  ]
                : [];
        }),
    };
}

function toStatementTransactionToAdd({
    financialDocumentId,
    date,
    amount,
    bankDescription,
}: TransactionForReconciliationFromStatement) {
    return { financialDocumentId, date, amount, bankDescription };
}

export function toPendingCheckMergesForBalance({
    checkMerges,
    outstandingCashItems,
}: ToPendingCheckMergesForBalanceParams): PendingCheckMergeForBalance[] {
    const outstandingById = new Map(
        outstandingCashItems.map((item) => [item.financialTransactionId, item]),
    );
    return checkMerges.flatMap((merge) => {
        const outstanding = outstandingById.get(merge.checkTransactionId);
        if (!outstanding) {
            return [];
        }
        const target =
            "bankTransactionId" in merge
                ? { bankTransactionId: merge.bankTransactionId }
                : { statementTransactionId: merge.statementTransactionId };
        return [
            {
                checkTransactionId: merge.checkTransactionId,
                amount: outstanding.amount,
                date: outstanding.date,
                ...target,
            },
        ];
    });
}

export function summarizeOutstandingCashItems(
    cashItems: OutstandingCashItem[],
): OutstandingCashItemsTotals {
    const [checks, deposits] = partition(
        cashItems,
        (item) => item.type === BankCashItemType.CHECK,
    );
    return {
        checks: {
            count: checks.length,
            amount: sumMonetaryValues(checks.map((item) => item.amount)),
        },
        deposits: {
            count: deposits.length,
            amount: sumMonetaryValues(deposits.map((item) => item.amount)),
        },
    };
}

export function getUnmatchedCashItems({
    cashItems,
    checkMerges,
}: GetUnmatchedCashItemsParams): OutstandingCashItem[] {
    const matchedIds = new Set(
        checkMerges.map((merge) => merge.checkTransactionId),
    );
    return cashItems.filter(
        (item) => !matchedIds.has(item.financialTransactionId),
    );
}

export function describeUnmatchedCashItems(
    cashItems: OutstandingCashItem[],
): string {
    const { checks, deposits } = summarizeOutstandingCashItems(cashItems);
    const items = [
        { count: checks.count, noun: "check" },
        { count: deposits.count, noun: "deposit" },
    ]
        .flatMap(({ count, noun }) =>
            count > 0 ? [pluralizeWithCount(noun, count)] : [],
        )
        .join(" and ");
    const verb = cashItems.length === 1 ? "is" : "are";

    return `${items} ${verb} still outstanding and will stay outstanding for a later reconciliation.`;
}
